import React, { useEffect, useState } from "react";
import { User, PhoneCall, Building2, HeartPulse, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDay, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill } from "../../components/Shared";

export default function CareFile() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [selParent, setSelParent] = useState("");
  const [form, setForm] = useState({ preferred_hospital: "", conditions: "", allergies: "", consent: false });
  const load = (pid = selParent) => api.get(`/family/care-file${pid ? `?parent_id=${pid}` : ""}`).then((r) => {
    setData(r.data);
    setForm({
      preferred_hospital: r.data.parent?.preferred_hospital || "",
      conditions: (r.data.medical?.conditions || []).join(", "),
      allergies: (r.data.medical?.allergies || []).join(", "),
      consent: !!r.data.medical?.consent?.medical_data,
    });
  });
  useEffect(() => { load().catch(() => setData({ empty: true })); }, []);
  const save = async () => {
    try {
      await api.put("/family/care-file", {
        parent_id: selParent || data.parent?.id,
        preferred_hospital: form.preferred_hospital,
        medical: { conditions: form.conditions.split(",").map((s) => s.trim()).filter(Boolean), allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean) },
        consent_medical: form.consent,
      });
      toast.success("Care file updated"); setEditing(false); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  if (!data) return <Loading />;
  if (data.empty) return <p className="text-stone-500">No care file yet.</p>;
  const { parent, medical, emergency_contacts, documents, visit_history } = data;
  return (
    <div data-testid="care-file-page">
      <PageHeader title="Parent Care File" sub="Your parent's structured care record. Access is permission-controlled and audited."
        actions={!data.restricted && <button onClick={() => setEditing(!editing)} className="p1-btn-outline !px-5 !py-2.5" data-testid="edit-care-file-btn">{editing ? "Cancel" : "Edit Care File"}</button>} />
      {data.all_parents?.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="parent-selector">
          {data.all_parents.map((p) => (
            <button key={p.id} onClick={() => { setSelParent(p.id); setEditing(false); load(p.id); }}
              className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${data.parent?.id === p.id ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"}`}
              data-testid={`parent-tab-${p.id}`}>
              {p.label ? `${p.label} — ` : ""}{p.name}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="p1-card p-6" data-testid="cf-identity">
            <div className="flex items-center gap-2"><User className="h-5 w-5 text-emerald-800" /><h2 className="font-heading text-lg font-semibold text-stone-900">Identity & Profile</h2></div>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[["Name", parent.name], ["Age", parent.age], ["Language", parent.preferred_language], ["Living", parent.living_arrangement], ["Area", parent.zone], ["City", parent.city]].map(([k, v]) => (
                <div key={k}><dt className="text-xs uppercase tracking-wider text-stone-400">{k}</dt><dd className="font-medium text-stone-800">{v || "—"}</dd></div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-stone-500">{typeof parent.address === "object" && parent.address !== null ? [parent.address.line1, parent.address.city, parent.address.pincode].filter(Boolean).join(", ") : parent.address}</p>
          </div>

          <div className="p1-card p-6" data-testid="cf-medical">
            <div className="flex items-center gap-2"><HeartPulse className="h-5 w-5 text-terracotta" /><h2 className="font-heading text-lg font-semibold text-stone-900">Medical Profile</h2></div>
            {editing ? (
              <div className="mt-4 space-y-4" data-testid="cf-edit-form">
                <div><label className="p1-label">Preferred hospital</label><input className="p1-input" value={form.preferred_hospital} onChange={(e) => setForm({ ...form, preferred_hospital: e.target.value })} data-testid="cf-edit-hospital" /></div>
                <div><label className="p1-label">Conditions (comma separated)</label><input className="p1-input" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} data-testid="cf-edit-conditions" /></div>
                <div><label className="p1-label">Allergies (comma separated)</label><input className="p1-input" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} data-testid="cf-edit-allergies" /></div>
                <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-800" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="cf-edit-consent" />
                  <span className="text-xs text-emerald-950">I consent to ParentOne storing this medical information for care coordination.</span>
                </label>
                <button onClick={save} className="p1-btn-primary !px-5 !py-2.5" data-testid="cf-save-btn">Save Changes</button>
              </div>
            ) : medical ? (
              <div className="mt-4 space-y-3 text-sm">
                <div><p className="text-xs uppercase tracking-wider text-stone-400">Conditions</p>
                  <div className="mt-1 flex flex-wrap gap-2">{medical.conditions?.map((c) => <span key={c} className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">{c}</span>)}</div></div>
                <div><p className="text-xs uppercase tracking-wider text-stone-400">Allergies</p>
                  <div className="mt-1 flex flex-wrap gap-2">{medical.allergies?.map((a) => <span key={a} className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">{a}</span>)}</div></div>
                <div><p className="text-xs uppercase tracking-wider text-stone-400">Medications</p>
                  <ul className="mt-1 space-y-1">{medical.medications?.map((m) => <li key={m.name} className="text-stone-700"><strong>{m.name}</strong> <span className="text-xs text-stone-500">— {m.schedule}</span></li>)}</ul></div>
                <div><p className="text-xs uppercase tracking-wider text-stone-400">Insurance</p>
                  <p className="text-stone-700">{medical.insurance?.provider} · {medical.insurance?.policy_number}</p></div>
                <p className="flex items-center gap-1.5 text-xs text-emerald-800"><ShieldCheck className="h-3.5 w-3.5" /> Stored with consent · access audited</p>
              </div>
            ) : data.restricted ? (
              <p className="mt-4 flex items-center gap-2 rounded-xl bg-stone-100 p-3 text-sm text-stone-500" data-testid="cf-restricted"><ShieldCheck className="h-4 w-4 shrink-0 text-stone-400" /> Medical details and documents are visible only to family members with full access.</p>
            ) : <p className="mt-4 text-sm text-stone-500">No medical information stored. It's optional and consent-based.</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="p1-card p-6" data-testid="cf-hospital">
            <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-emerald-800" /><h2 className="font-heading text-lg font-semibold text-stone-900">Preferred Hospital & Doctors</h2></div>
            <p className="mt-3 text-sm font-semibold text-stone-800">{parent.preferred_hospital || "—"}</p>
            <ul className="mt-2 space-y-1 text-sm text-stone-600">
              {(parent.doctors || []).map((d) => <li key={d.name}>{d.name} · {d.specialty} · {d.phone}</li>)}
            </ul>
          </div>
          <div className="p1-card p-6" data-testid="cf-contacts">
            <div className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-terracotta" /><h2 className="font-heading text-lg font-semibold text-stone-900">Emergency Contacts</h2></div>
            <ul className="mt-3 space-y-2">
              {emergency_contacts.sort((a, b) => a.priority - b.priority).map((c) => (
                <li key={c.id} className="flex justify-between text-sm"><span className="text-stone-700">{c.name} <span className="text-xs text-stone-400">({c.relation})</span></span><span className="font-semibold text-emerald-900">{c.phone}</span></li>
              ))}
            </ul>
          </div>
          <div className="p1-card p-6" data-testid="cf-documents">
            <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-800" /><h2 className="font-heading text-lg font-semibold text-stone-900">Documents ({documents.length})</h2></div>
            <ul className="mt-3 space-y-2 text-sm">
              {documents.map((d) => <li key={d.id} className="flex justify-between"><span className="text-stone-700">{d.name}</span><span className="text-xs capitalize text-stone-400">{d.category.replace(/_/g, " ")}</span></li>)}
            </ul>
          </div>
          <div className="p1-card p-6" data-testid="cf-history">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Visit History</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {visit_history.map((v) => (
                <li key={v.id} className="flex items-center justify-between"><span className="text-stone-700">{v.purpose} · {fmtDay(v.scheduled_at)}</span><StatusPill status={v.status} /></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
